import React, { useEffect, useCallback, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../Header';
import SiteFooter from '../SiteFooter';
import Sidebar from './Sidebar';
import './DashboardLayout.css';

const DashboardLayout: React.FC = () => {
  const { isAuthenticated, isAuthReady } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const updateHeaderHeight = useCallback(() => {
    const header = document.querySelector('header');
    if (header) {
      document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    }
  }, []);

  useEffect(() => {
    const initialTimeout = setTimeout(updateHeaderHeight, 100);
    const header = document.querySelector('header');
    if (!header) {
      return () => clearTimeout(initialTimeout);
    }
    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(header);
    window.addEventListener('resize', updateHeaderHeight);
    return () => {
      clearTimeout(initialTimeout);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, [updateHeaderHeight]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (!isAuthReady) {
    return (
      <div className="dashboard-loading">
        <div className="loading">Loading…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <div className="dashboard-page">
      <Header subtitle="Dashboard" />
      <div className="dashboard-body">
        <div className="dashboard-cluster">
          <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <main className="dashboard-main">
            <Outlet />
            <SiteFooter />
          </main>
        </div>
      </div>
      <button
        type="button"
        className="dashboard-nav-toggle"
        onClick={() => setSidebarOpen((v) => !v)}
        aria-label="Toggle navigation"
      >
        Menu
      </button>
      {sidebarOpen && (
        <button
          type="button"
          className="dashboard-nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
